import dayjs from 'dayjs';
import { useState } from 'react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import ActivityStatusBadge from '@/modules/common/components/ActivityStatusBadge.tsx';
import { CreatedByAiLabel } from '@/modules/common/components/CreatedByAiLabel.tsx';
import { useRegisterTypeStrings } from '@/modules/common/hooks/useRegisterTypeStrings.tsx';
import { DividendOperationInfoModal } from '@/modules/issuer-dashboard/components/distribution-dashboard/DividendOperationInfoModal.tsx';
import { IssuerActivitiesOperationInfoModal } from '@/modules/issuer-dashboard/components/modals/IssuerActivitiesOperationInfoModal.tsx';
import {
  InfoItem,
  IssuerTransactionsOperationInfoModal,
} from '@/modules/issuer-dashboard/components/modals/IssuerTransactionsOperationInfoModal.tsx';
import { mapCashMovementTypeToText } from '@/modules/issuer-dashboard/components/portfolios/IssuerAddCashMovementModal.tsx';
import { IssuerPortfolioTransactionFullInfoModal } from '@/modules/issuer-dashboard/components/portfolios/IssuerPortfolioTransactionFullInfoModal.tsx';
import { mapPortfolioTransactionType } from '@/modules/issuer-dashboard/components/portfolios/Tabs/PortfolioTransactionsTab.tsx';
import { issuerDashboardPaths } from '@/modules/issuer-dashboard/routes.tsx';
import { BaseModalProps, Button, ModalContentWrapper, TooltipNew } from 'ui';
import { fixNameEnum, goodFormatNumberCustom } from 'ui/utils';
import { PaymentStatus, PortfolioCashMovementPaginateResponse } from 'api';

interface IssuerCashMovementFullInfoModalProps extends BaseModalProps {
  item: PortfolioCashMovementPaginateResponse;
  status: string;
  onConfirm?: () => void;
  onReject?: () => void;
  registerId: string;
  registerCountry: string;
}

export const IssuerCashMovementFullInfoModal = ({
  item,
  status,
  onConfirm,
  onReject,
  open,
  onClose,
  registerId,
  registerCountry,
}: IssuerCashMovementFullInfoModalProps) => {
  const { pathname } = useLocation();
  const [params] = useSearchParams();
  const [openDividend, setOpenDividend] = useState(false);
  const [openTransactionInfo, setOpenTransactionInfo] = useState(false);
  const getRegisterTypeStrings = useRegisterTypeStrings();
  const requestedAtDate = item?.requested_at_date
    ? dayjs(item?.requested_at_date).format('DD-MM-YYYY HH:mm')
    : '-';
  const confirmationDate = item?.confirmation_date
    ? dayjs(item?.confirmation_date).format('DD-MM-YYYY HH:mm')
    : '';
  const currency = (item?.to_bank?.bank_currency || item?.from_bank?.bank_currency)?.toUpperCase();
  return (
    <ModalContentWrapper variant="right" title="Movement info" open={open} onClose={onClose}>
      {item && (
        <>
          <div className="flex flex-col justify-between gap-4 h-full">
            <div className="space-y-3">
              <InfoItem title="ID">
                <div>#{item.id}</div>
              </InfoItem>
              {!!item.transaction_id && (
                <InfoItem title="Transaction ID">
                  <div
                    onClick={() => setOpenTransactionInfo(true)}
                    className="cursor-pointer hover:underline text-blue-700"
                  >
                    #{item.transaction_id}
                  </div>
                </InfoItem>
              )}
              {!!item.portfolio_transaction?.id && (
                <InfoItem title="Portfolio transaction ID">
                  <div
                    onClick={() => setOpenTransactionInfo(true)}
                    className="cursor-pointer hover:underline text-blue-700"
                  >
                    #{item.portfolio_transaction?.id}
                  </div>
                </InfoItem>
              )}
              {!!item.payment_id && !!item.dividend_event && (
                <InfoItem
                  title={`${getRegisterTypeStrings('Dividend', {
                    capitalize: true,
                  })} payment ID`}
                >
                  <div
                    onClick={() => setOpenTransactionInfo(true)}
                    className="cursor-pointer hover:underline text-blue-700"
                  >
                    #{item.payment_id}
                  </div>
                </InfoItem>
              )}
              {!!item.dividend_event_id && (
                <InfoItem
                  title={`${getRegisterTypeStrings('dividend', { capitalize: true })} event ID`}
                >
                  <div
                    className="text-blue-700 cursor-pointer hover:underline"
                    onClick={() => setOpenDividend(true)}
                  >
                    #{item.dividend_event_id}
                  </div>
                </InfoItem>
              )}
              <InfoItem title="Status">
                <ActivityStatusBadge status={status} />
              </InfoItem>
              {/* <InfoItem title="Class"> */}
              {/*   <div>{item.register_class_name ?? '-'}</div> */}
              {/* </InfoItem> */}

              <InfoItem title="Type">
                <span className="flex gap-2 items-center">
                  <span>{mapCashMovementTypeToText[item.direction]}</span>
                  <CreatedByAiLabel aiStatus={item.ai_status} />
                  {(item?.transaction?.type ||
                    item?.dividend_event?.type ||
                    item?.portfolio_transaction?.transaction_type) && (
                    <span className="text-gray-500 text-xs">
                      (
                      {item?.portfolio_transaction?.transaction_type
                        ? mapPortfolioTransactionType[item?.portfolio_transaction?.transaction_type]
                        : fixNameEnum(
                            item?.transaction?.type ||
                              getRegisterTypeStrings('Dividend', { capitalize: true }),
                          )}
                      )
                    </span>
                  )}
                </span>
              </InfoItem>
              <InfoItem title="Sender">
                <span className="flex gap-2 items-center font-[Inter] text-sm text-nowrap">
                  {item?.from_bank ? (
                    <>
                      <span className="flex gap-1 items-center font-[Inter] text-sm text-nowrap">
                        <span className="text-gray-900 whitespace-nowrap max-w-[170px] overflow-hidden text-ellipsis">
                          {item?.from_bank?.bank_account_name}
                        </span>
                        {/* <span className="text-gray-500"> */}
                        {/*   ({item?.from_bank?.bank_name.toUpperCase()}) */}
                        {/* </span> */}
                      </span>
                      <span className="text-gray-400">
                        {item?.from_bank?.bank_currency.toUpperCase()}
                      </span>
                    </>
                  ) : item?.shareholder_entity?.entity_name ? (
                    <>
                      <Link
                        state={{ from: pathname + '?' + params.toString() }}
                        to={{
                          pathname: issuerDashboardPaths.dynamic.holder(
                            (item.shareholder_entity?.id || '0').toString(),
                          ),
                          search: params.toString(),
                        }}
                        className="text-blue-700 hover:underline whitespace-nowrap max-w-[230px] overflow-hidden text-ellipsis"
                      >
                        {item?.shareholder_entity?.entity_name}
                      </Link>
                    </>
                  ) : item?.shareholder_user ? (
                    <>
                      <span className="text-gray-900 whitespace-nowrap max-w-[230px] overflow-hidden text-ellipsis">
                        {item?.shareholder_user?.first_name} {item?.shareholder_user?.last_name}
                      </span>
                    </>
                  ) : (
                    <>-</>
                  )}
                </span>
              </InfoItem>
              <InfoItem title="Destination">
                <span className="flex gap-2 items-center font-[Inter] text-sm text-nowrap">
                  {item?.to_bank ? (
                    <>
                      <span className="flex gap-1 items-center font-[Inter] text-sm text-nowrap">
                        <span className="text-gray-900 whitespace-nowrap max-w-[170px] overflow-hidden text-ellipsis">
                          {item?.to_bank?.bank_account_name}
                        </span>
                        {/* <span className="text-gray-500"> */}
                        {/*   ({item?.to_bank?.bank_name.toUpperCase()}) */}
                        {/* </span> */}
                      </span>
                      <span className="text-gray-400">
                        {item?.to_bank?.bank_currency.toUpperCase()}
                      </span>
                    </>
                  ) : item?.shareholder_entity?.entity_name ? (
                    <>
                      <Link
                        state={{ from: pathname + '?' + params.toString() }}
                        to={{
                          pathname: issuerDashboardPaths.dynamic.holder(
                            (item.shareholder_entity?.id || '0').toString(),
                          ),
                          search: params.toString(),
                        }}
                        className="text-blue-700 hover:underline whitespace-nowrap max-w-[230px] overflow-hidden text-ellipsis"
                      >
                        {item?.shareholder_entity?.entity_name}
                      </Link>
                    </>
                  ) : item?.shareholder_user ? (
                    <>
                      <span className="text-gray-900 whitespace-nowrap max-w-[230px] overflow-hidden text-ellipsis">
                        {item?.shareholder_user?.first_name} {item?.shareholder_user?.last_name}
                      </span>
                    </>
                  ) : (
                    <>-</>
                  )}
                </span>
              </InfoItem>
              {/* <InfoItem title="Holder"> */}
              {/*   <Link */}
              {/*     state={{ from: pathname + '?' + params.toString() }} */}
              {/*     to={{ */}
              {/*       pathname: issuerDashboardPaths.dynamic.holder( */}
              {/*         (item.from_entity_id || '0').toString(), */}
              {/*       ), */}
              {/*       search: params.toString(), */}
              {/*     }} */}
              {/*     className="text-blue-700 hover:underline" */}
              {/*   > */}
              {/*     {item.from_entity_name} */}
              {/*   </Link> */}
              {/* </InfoItem> */}
              <InfoItem title="Amount">
                <div>{goodFormatNumberCustom(item.amount, currency, 0, 2)}</div>
              </InfoItem>
              {!!item.fx_rate && (
                <InfoItem title="Exchange Rate">
                  <TooltipNew
                    text={`${goodFormatNumberCustom(
                      item.fx_rate,
                      item?.to_bank?.bank_currency?.toUpperCase(),
                    )} = ${goodFormatNumberCustom(
                      1,
                      item?.from_bank?.bank_currency?.toUpperCase(),
                    )}`}
                  >
                    <div>
                      {goodFormatNumberCustom(
                        item.fx_rate,
                        item?.to_bank?.bank_currency?.toUpperCase(),
                      )}
                    </div>
                  </TooltipNew>
                </InfoItem>
              )}
              <InfoItem title="Requested date">
                <div>{requestedAtDate}</div>
              </InfoItem>
              {!!confirmationDate && (
                <InfoItem title="Confirmation date">
                  <div>{confirmationDate}</div>
                </InfoItem>
              )}

              {!!item.memo && (
                <InfoItem title="Memo">
                  <div>{item.memo}</div>
                </InfoItem>
              )}
            </div>
            <div className="flex flex-col gap-4 w-full">
              {status === PaymentStatus.Pending && (
                <>
                  {!!onConfirm && (
                    <Button
                      className="min-w-[90px]"
                      onClick={() => {
                        onConfirm && onConfirm();
                        onClose();
                      }}
                    >
                      <span>Confirm</span>
                    </Button>
                  )}

                  {!!onReject && (
                    <Button
                      variant="secondary"
                      className="min-w-[90px]"
                      onClick={() => {
                        onReject && onReject();
                        onClose();
                      }}
                    >
                      Decline
                    </Button>
                  )}
                </>
              )}
            </div>
          </div>
          {!!item?.transaction_id && (
            <IssuerTransactionsOperationInfoModal
              id={item?.transaction_id}
              onClose={() => setOpenTransactionInfo(false)}
              open={openTransactionInfo}
              registerCountry={registerCountry}
              registerId={registerId}
            />
          )}
          {!!item?.portfolio_transaction?.id && (
            <IssuerPortfolioTransactionFullInfoModal
              portfolioId={item?.portfolio_transaction?.portfolio_id}
              id={item?.portfolio_transaction?.id}
              onClose={() => setOpenTransactionInfo(false)}
              open={openTransactionInfo}
            />
          )}
          {!!item?.payment_id && (
            <IssuerActivitiesOperationInfoModal
              paymentId={item?.payment_id}
              registerId={registerId}
              registerCountry={registerCountry}
              open={openTransactionInfo}
              onClose={() => setOpenTransactionInfo(false)}
            />
          )}
          {!!item?.dividend_event_id && openDividend && (
            <DividendOperationInfoModal
              open={openDividend}
              id={item?.dividend_event_id}
              onClose={() => setOpenDividend(false)}
              registerId={registerId}
              registerCountry={registerCountry}
            />
          )}
        </>
      )}
    </ModalContentWrapper>
  );
};
